import './ComicForm.css';
import React, { useEffect, useRef, useState } from 'react';

import { useLanguage } from '../../hooks/useLanguage';
import en from './translations/en';
import sr from './translations/sr';
import sl from './translations/sl';

import {
  MAX_COMIC_DESCRIPTION_CHARACTERS,
  MAX_REGENERATION_INSTRUCTION_CHARACTERS,
  MIN_COMIC_DESCRIPTION_CHARACTERS,
  BUBBLE_MESSAGE_CHARACTER_LIMIT_FOR_ONE_PAGE_COMIC,
  COMIC_TITLE_CHARACTER_LIMIT,
  DEFAULT_CHARACTER_ID,
} from '../../constants/globalConstants';
import { COMIC_LANGUAGES } from './constants/languages';
import { INPUT_TYPES } from './constants/inputTypes';
import { generateComicAsync, generateComicDialogAsync } from './comicApi';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaChevronDown,
  FaCloudUploadAlt,
  FaPen,
  FaQuestionCircle,
  FaSyncAlt,
  FaTimes,
} from 'react-icons/fa';
import { REQUIRED_CREDITS } from './constants/credits';
import { consumeLandingDraft } from '../landing/landingDraft';
import { ComicStoryboard } from './comicTypes';
import {
  useComicStoryboard,
  getOverallVerificationScore,
} from './useComicStoryboard';
import { asIcon } from '../icons';
import AppShell, { useAppShell } from '../appShell/AppShell';
const RedeemCloseIcon = asIcon(FaTimes);
const WizardCheckIcon = asIcon(FaCheck);
const WizardVerificationUnknownIcon = asIcon(FaQuestionCircle);
const WizardVerificationChevronIcon = asIcon(FaChevronDown);
const UploadIcon = asIcon(FaCloudUploadAlt);
const DescribeIcon = asIcon(FaPen);
const RegenerateIcon = asIcon(FaSyncAlt);
const PreviousIcon = asIcon(FaArrowLeft);
const NextIcon = asIcon(FaArrowRight);

type RewriteLength = 'keep' | 'shorter' | 'longer';
type RewriteComplexity = 'keep' | 'simpler' | 'scientific';
type RewriteTone = 'professional' | 'friendly' | 'funny';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TEXT_COLOR = '#111111';
const PANEL_COUNT = 4;
const MAX_PANEL_IMAGE_SIZE = 1200;
const PANEL_IMAGE_JPEG_QUALITY = 0.86;
const COLOR_PRESETS = [
  '#ffffff',
  '#f7f1ef',
  '#f8eac7',
  '#d8edf8',
  '#dcefe4',
  '#f6dfeb',
  '#2f4858',
  '#111111',
];
const FONT_OPTIONS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: '"Times New Roman"', label: 'Times New Roman' },
  { value: 'Verdana', label: 'Verdana' },
  { value: '"Trebuchet MS"', label: 'Trebuchet MS' },
  { value: '"Courier New"', label: 'Courier New' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: 'Impact', label: 'Impact' },
  { value: '"Comic Sans MS"', label: 'Comic Sans MS' },
  { value: '"Palatino Linotype"', label: 'Palatino Linotype' },
  { value: 'Garamond', label: 'Garamond' },
];
const DEFAULT_TITLE_FONT = 'Impact';
const DEFAULT_BODY_FONT = '"Comic Sans MS"';

const normalizeHexColor = (value: string) => {
  const normalized = value.trim();
  const match = normalized.match(/^#?([0-9a-f]{6})$/i);

  return match ? `#${match[1].toLowerCase()}` : normalized;
};

const getValidColorInputValue = (value: string, fallback: string) => {
  const normalized = normalizeHexColor(value);

  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback;
};

const getRgbFromHex = (hexColor: string) => {
  const normalized = normalizeHexColor(hexColor);
  const match = normalized.match(/^#([0-9a-f]{6})$/i);

  if (!match) return { r: 255, g: 255, b: 255 };

  const value = match[1];

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const getHexFromRgb = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((value) =>
      Math.max(0, Math.min(255, Number.isFinite(value) ? value : 0))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

const getImageCreditText = (
  template: string,
  generatedImageCount: number,
  creditCost: number,
  imageCountTextByCount: Record<number, string>,
  tokenCountTextByCount: Record<number, string>,
) => {
  const imageCountText =
    imageCountTextByCount[generatedImageCount] ?? String(generatedImageCount);
  const tokenCountText =
    tokenCountTextByCount[creditCost] ?? String(creditCost);

  return template
    .replace('__IMAGE_COUNT__', imageCountText)
    .replace('__TOKEN_COUNT__', tokenCountText)
    .replace('__COUNT__', String(generatedImageCount))
    .replace('__CREDITS__', String(creditCost));
};

const getSuggestedTextColor = (backgroundColor: string) => {
  const { r, g, b } = getRgbFromHex(backgroundColor);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.58 ? '#111111' : '#ffffff';
};

const clampComicTitle = (title: string) =>
  title.slice(0, COMIC_TITLE_CHARACTER_LIMIT);

interface ComicFormContentProps {
  translations: typeof en;
}

const ComicFormContent: React.FC<ComicFormContentProps> = ({
  translations,
}) => {
  const { credits, refreshTokenBalance: loadTokenBalance } = useAppShell();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedFrame = 0;
  const [currentStep, setCurrentStep] = useState(1);
  const [isSourceDragging, setIsSourceDragging] = useState(false);
  const [regeneratingFrameIndex, setRegeneratingFrameIndex] = useState<
    number | null
  >(null);
  const [rewriteInstruction, setRewriteInstruction] = useState('');
  const [rewriteLength, setRewriteLength] = useState<RewriteLength>('keep');
  const [rewriteComplexity, setRewriteComplexity] =
    useState<RewriteComplexity>('keep');
  const [rewriteTone, setRewriteTone] = useState<RewriteTone>('professional');
  const [workflowError, setWorkflowError] = useState('');
  const [inputType, setInputType] = useState(INPUT_TYPES.upload);
  const [file, setFile] = useState<File | null>(null);
  const [comicDescription, setComicDescription] = useState('');
  const [isVerificationDetailsOpen, setIsVerificationDetailsOpen] =
    useState(false);
  const bubbleMessageCharacterLimit =
    BUBBLE_MESSAGE_CHARACTER_LIMIT_FOR_ONE_PAGE_COMIC;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isDialogGenerationInProgress, setIsDialogGenerationInProgress] =
    useState(false);
  const [isComicGenerationInProgress, setIsComicGenerationInProgress] =
    useState(false);
  const [comicLanguage, setComicLanguage] = useState(COMIC_LANGUAGES.Serbian);
  const {
    storyboard,
    messages,
    inputErrors,
    verificationDetailScores,
    acceptStoryboard,
    updateText,
    updateTitle,
  } = useComicStoryboard(
    inputType === INPUT_TYPES.upload ? file : comicDescription,
    comicLanguage,
  );
  const characterId = DEFAULT_CHARACTER_ID;
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_BACKGROUND_COLOR,
  );
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [titleFont, setTitleFont] = useState(DEFAULT_TITLE_FONT);
  const [bodyFont, setBodyFont] = useState(DEFAULT_BODY_FONT);
  const [panelImages, setPanelImages] = useState<(string | null)[]>(
    Array(PANEL_COUNT).fill(null),
  );
  const [panelImageNames, setPanelImageNames] = useState<string[]>(
    Array(PANEL_COUNT).fill(''),
  );
  const [panelImagePrompts, setPanelImagePrompts] = useState<string[]>(
    Array(PANEL_COUNT).fill(''),
  );
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);
  useEffect(() => {
    consumeLandingDraft()
      .then((draft) => {
        if (!draft) return;

        setInputType(draft.inputType);
        setComicDescription(draft.description);
        setFile(draft.file);
        if (draft.comicLanguage) {
          setComicLanguage(draft.comicLanguage);
        }
        setCurrentStep(1);
      })
      .catch((error) => {
        console.error('Could not restore landing page draft:', error);
      });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile?.type === 'application/pdf') setFile(selectedFile);
  };

  const readFileAsDataUrl = (selectedFile: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(selectedFile);
    });

  const loadImageElement = (imageSource: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();

      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not load panel image.'));
      image.src = imageSource;
    });

  const optimizePanelImage = async (selectedFile: File) => {
    const imageSource = await readFileAsDataUrl(selectedFile);
    const image = await loadImageElement(imageSource);
    const scale = Math.min(
      1,
      MAX_PANEL_IMAGE_SIZE / Math.max(image.width, image.height),
    );
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return imageSource;

    canvas.width = width;
    canvas.height = height;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', PANEL_IMAGE_JPEG_QUALITY);
  };

  const handlePanelImageChange = async (
    panelIndex: number,
    selectedFile: File | undefined,
  ) => {
    if (!selectedFile?.type.startsWith('image/')) return;

    const imageDataUrl = await optimizePanelImage(selectedFile);

    setPanelImages((currentImages) =>
      currentImages.map((image, index) =>
        index === panelIndex ? imageDataUrl : image,
      ),
    );
    setPanelImageNames((currentNames) =>
      currentNames.map((name, index) =>
        index === panelIndex ? selectedFile.name : name,
      ),
    );
  };

  const handleRemovePanelImage = (panelIndex: number) => {
    setPanelImages((currentImages) =>
      currentImages.map((image, index) =>
        index === panelIndex ? null : image,
      ),
    );
    setPanelImageNames((currentNames) =>
      currentNames.map((name, index) => (index === panelIndex ? '' : name)),
    );
  };

  const handlePanelImagePromptChange = (panelIndex: number, value: string) => {
    setPanelImagePrompts((currentPrompts) =>
      currentPrompts.map((prompt, index) =>
        index === panelIndex ? value : prompt,
      ),
    );
  };

  const handleMessageChange = (
    pageIndex: number,
    messageIndex: number,
    value: string,
  ) => {
    if (pageIndex !== 0) return;
    updateText(messageIndex, value);
    setPdfUrl(null);
  };

  const handleTitleChange = (value: string) => {
    updateTitle(value);
    setPdfUrl(null);
  };

  const handleRgbColorChange = (
    colorType: 'background' | 'text',
    channel: 'r' | 'g' | 'b',
    value: string,
  ) => {
    const currentColor =
      colorType === 'background' ? backgroundColor : textColor;
    const rgb = getRgbFromHex(currentColor);
    const channelValue = Number.parseInt(value, 10);
    const nextColor = getHexFromRgb(
      channel === 'r' ? channelValue : rgb.r,
      channel === 'g' ? channelValue : rgb.g,
      channel === 'b' ? channelValue : rgb.b,
    );

    if (colorType === 'background') {
      setBackgroundColor(nextColor);
      return;
    }

    setTextColor(nextColor);
  };

  const suggestedTextColor = getSuggestedTextColor(backgroundColor);
  const titleLength = storyboard.title.trim().length;
  const isTitleLimitExceeded = titleLength > COMIC_TITLE_CHARACTER_LIMIT;

  const createDialogFormData = () => {
    const formData = new FormData();

    formData.append('language', comicLanguage);

    if (inputType === INPUT_TYPES.upload && file) formData.append('file', file);

    if (inputType === INPUT_TYPES.description && comicDescription.trim())
      formData.append('comicDescription', comicDescription);

    return formData;
  };

  const applyGeneratedStoryboard = (next: ComicStoryboard) => {
    acceptStoryboard(next);
    setPdfUrl(null);
    setPanelImages(Array(PANEL_COUNT).fill(null));
    setPanelImageNames(Array(PANEL_COUNT).fill(''));
    setPanelImagePrompts(Array(PANEL_COUNT).fill(''));
    setIsVerificationDetailsOpen(false);
  };

  const handleGenerateDialog = async () => {
    setIsDialogGenerationInProgress(true);
    setWorkflowError('');

    try {
      const generatedStoryboard = await generateComicDialogAsync(
        createDialogFormData(),
      );
      applyGeneratedStoryboard(generatedStoryboard);
      setCurrentStep(2);

      await loadTokenBalance();
    } catch (error) {
      console.error('Error generating comic dialog:', error);
      setWorkflowError(translations.workflowError);
    } finally {
      setIsDialogGenerationInProgress(false);
    }
  };

  const handleRegenerateFrame = async () => {
    setRegeneratingFrameIndex(0);
    setWorkflowError('');

    const formData = createDialogFormData();
    formData.append('currentStoryboard', JSON.stringify(storyboard));
    formData.append('manualDialogMessages', JSON.stringify(messages));
    formData.append('regenerationInstruction', rewriteInstruction.trim());
    formData.append('lengthPreference', rewriteLength);
    formData.append('complexityPreference', rewriteComplexity);
    formData.append('tonePreference', rewriteTone);

    try {
      const regeneratedStoryboard = await generateComicDialogAsync(formData);
      applyGeneratedStoryboard(regeneratedStoryboard);
      setRewriteInstruction('');
      await loadTokenBalance();
    } catch (error) {
      console.error('Error regenerating comic storyboard:', error);
      setWorkflowError(translations.workflowError);
    } finally {
      setRegeneratingFrameIndex(null);
    }
  };

  const handleSubmit = async () => {
    setIsComicGenerationInProgress(true);
    setWorkflowError('');

    const editedStoryboard: ComicStoryboard = {
      ...storyboard,
      title:
        clampComicTitle(storyboard.title.trim()) ||
        translations.defaultComicTitle,
      panels: storyboard.panels.map((panel, index) => ({
        ...panel,
        text: messages[0]?.[index] ?? panel.text,
      })),
    };
    const data = {
      oneColor: 'true',
      colorVariant: 'blueVariantHI',
      storyboard: editedStoryboard,
      characterId,
      backgroundColor,
      textColor,
      titleFont,
      bodyFont,
      panelImages,
      panelImagePrompts,
    };

    try {
      const generator = generateComicAsync(data);

      for await (const generatedComic of generator) {
        setPdfUrl(generatedComic.pdfUrl);
        if (generatedComic.panelImages.length) {
          setPanelImages((currentImages) =>
            currentImages.map(
              (image, index) => generatedComic.panelImages[index] ?? image,
            ),
          );
          setPanelImageNames((currentNames) =>
            currentNames.map((name, index) =>
              name || generatedComic.panelImages[index]
                ? name || translations.generatedPanelImage
                : name,
            ),
          );
        }
      }

      await loadTokenBalance();
    } catch (error) {
      console.error('Error generating comic:', error);
      setWorkflowError(translations.workflowError);
    } finally {
      setIsComicGenerationInProgress(false);
    }
  };

  const generatedPanelImageCount = panelImages.filter((image) => !image).length;
  const comicGenerationCreditCost =
    generatedPanelImageCount * REQUIRED_CREDITS.GENERATE_COMIC_IMAGE;
  const imageStepCreditNotice =
    comicGenerationCreditCost > 0
      ? getImageCreditText(
          translations.imageStepCreditNotice,
          generatedPanelImageCount,
          comicGenerationCreditCost,
          translations.imageCountText,
          translations.tokenCountText,
        )
      : translations.imageStepFreeNotice;

  const isGenerateDialogDisabled =
    isDialogGenerationInProgress ||
    isComicGenerationInProgress ||
    (inputType === INPUT_TYPES.upload && !file) ||
    (inputType === INPUT_TYPES.description &&
      (!comicDescription ||
        comicDescription.length < MIN_COMIC_DESCRIPTION_CHARACTERS)) ||
    (credits != null && credits < REQUIRED_CREDITS.GENERATE_DIALOG);

  const isGenerateDisabled =
    isComicGenerationInProgress ||
    isDialogGenerationInProgress ||
    messages[0]?.length !== 4 ||
    isTitleLimitExceeded ||
    messages[0]?.some((message) => !message.trim()) ||
    inputErrors.some((pageErrors) => pageErrors.includes(true)) ||
    (credits != null && credits < comicGenerationCreditCost);

  const overallVerificationScore = verificationDetailScores
    ? getOverallVerificationScore(verificationDetailScores)
    : null;

  const verificationChecklist = verificationDetailScores
    ? [
        {
          key: 'source',
          score: verificationDetailScores.source,
          label: translations.verificationDetailSource,
          tooltip: translations.verificationDetailSourceTooltip,
        },
        {
          key: 'claims',
          score: verificationDetailScores.claims,
          label: translations.verificationDetailClaims,
          tooltip: translations.verificationDetailClaimsTooltip,
        },
        {
          key: 'language',
          score: verificationDetailScores.language,
          label: translations.verificationDetailLanguage,
          tooltip: translations.verificationDetailLanguageTooltip,
        },
      ]
    : [];

  return (
    <div className="content-shell">
      <div className={`comic-wizard-card wizard-step-${currentStep}`}>
        <div className="content-header">
          <img
            src="/scicomic-logo-white.png"
            alt="Scicomic Logo"
            className="content-logo"
          />
        </div>

        <p className="content-subtitle">{translations.disclaimer}</p>

        <div
          className="wizard-progress"
          aria-label={`Step ${currentStep} of 4`}
        >
          {[1, 2, 3, 4].map((step, index) => (
            <React.Fragment key={step}>
              {index > 0 && (
                <span
                  className={`wizard-progress-line ${step <= currentStep ? 'complete' : ''}`}
                />
              )}
              <button
                type="button"
                className={`wizard-progress-dot ${step === currentStep ? 'active' : ''} ${step < currentStep ? 'complete' : ''}`}
                onClick={() => step < currentStep && setCurrentStep(step)}
                disabled={step > currentStep}
                aria-label={`${translations.wizardStep} ${step}`}
              >
                {step < currentStep ? <WizardCheckIcon /> : step}
              </button>
            </React.Fragment>
          ))}
        </div>

        <header className="wizard-heading">
          <h1>
            {currentStep === 1 && translations.wizardSource}
            {currentStep === 2 && translations.wizardScript}
            {currentStep === 3 && translations.wizardImages}
            {currentStep === 4 && translations.wizardVisualDesign}
          </h1>
          <p>
            {currentStep === 1 && translations.wizardSourceDescription}
            {currentStep === 2 && translations.wizardScriptDescription}
            {currentStep === 3 && translations.wizardImagesDescription}
            {currentStep === 4 && translations.wizardVisualDescription}
          </p>
        </header>

        {workflowError && <p className="wizard-error">{workflowError}</p>}

        {currentStep === 1 && (
          <section className="wizard-panel wizard-source-panel">
            <p className="wizard-field-label">{translations.sourceQuestion}</p>
            <div className="wizard-source-tabs">
              <button
                type="button"
                className={inputType === INPUT_TYPES.upload ? 'active' : ''}
                onClick={() => setInputType(INPUT_TYPES.upload)}
              >
                <UploadIcon /> {translations.uploadPDF}
              </button>
              <button
                type="button"
                className={
                  inputType === INPUT_TYPES.description ? 'active' : ''
                }
                onClick={() => setInputType(INPUT_TYPES.description)}
              >
                <DescribeIcon /> {translations.comicDescription}
              </button>
            </div>

            {inputType === INPUT_TYPES.upload && (
              <div
                className={`wizard-dropzone ${isSourceDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={() => setIsSourceDragging(true)}
                onDragLeave={() => setIsSourceDragging(false)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsSourceDragging(false);
                  const droppedFile = event.dataTransfer.files[0];
                  if (droppedFile?.type === 'application/pdf')
                    setFile(droppedFile);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ')
                    fileInputRef.current?.click();
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept="application/pdf,.pdf"
                  className="wizard-file-input"
                />
                <UploadIcon />
                <strong>
                  {file ? (
                    file.name
                  ) : (
                    <>
                      {translations.dropPdf}{' '}
                      {translations.browsePdf && (
                        <span>{translations.browsePdf}</span>
                      )}
                    </>
                  )}
                </strong>
              </div>
            )}

            {inputType === INPUT_TYPES.description && (
              <div className="wizard-description-field">
                <textarea
                  value={comicDescription}
                  onChange={(e) => setComicDescription(e.target.value)}
                  placeholder={translations.comicDescriptionPlaceholder}
                  maxLength={MAX_COMIC_DESCRIPTION_CHARACTERS}
                  className={
                    comicDescription.length > 0 &&
                    comicDescription.length < MIN_COMIC_DESCRIPTION_CHARACTERS
                      ? 'input-error'
                      : ''
                  }
                />
                <span className="wizard-character-counter">
                  {comicDescription.length}/{MAX_COMIC_DESCRIPTION_CHARACTERS}
                </span>
                {comicDescription.length > 0 &&
                  comicDescription.length <
                    MIN_COMIC_DESCRIPTION_CHARACTERS && (
                    <p className="error-message">
                      {translations.comicDescriptionErrorMessage}
                    </p>
                  )}
              </div>
            )}

            <div className="wizard-source-settings">
              <div className="wizard-field">
                <label className="form-label no-wrap input-label">
                  {translations.language}
                </label>
                <select
                  value={comicLanguage}
                  onChange={(e) => setComicLanguage(e.target.value)}
                  className="form-select"
                >
                  <option value={COMIC_LANGUAGES.Serbian}>
                    {translations.languageSerbian}
                  </option>
                  <option value={COMIC_LANGUAGES.Slovenian}>
                    {translations.languageSlovenian}
                  </option>
                  <option value={COMIC_LANGUAGES.English}>
                    {translations.languageEnglish}
                  </option>
                  <option value={COMIC_LANGUAGES.Spanish}>
                    {translations.languageSpanish}
                  </option>
                  <option value={COMIC_LANGUAGES.French}>
                    {translations.languageFrench}
                  </option>
                  <option value={COMIC_LANGUAGES.Russian}>
                    {translations.languageRussian}
                  </option>
                  <option value={COMIC_LANGUAGES.Italian}>
                    {translations.languageItalian}
                  </option>
                  <option value={COMIC_LANGUAGES.Chinese}>
                    {translations.languageChinese}
                  </option>
                  <option value={COMIC_LANGUAGES.Turkish}>
                    {translations.languageTurkish}
                  </option>
                  <option value={COMIC_LANGUAGES.Hungarian}>
                    {translations.languageHungarian}
                  </option>
                  <option value={COMIC_LANGUAGES.Portuguese}>
                    {translations.languagePortuguese}
                  </option>
                </select>
              </div>

              <div className="wizard-field">
                <label>{translations.comicType}</label>
                <div className="wizard-comic-type-card" aria-current="true">
                  <div className="wizard-template-preview" aria-hidden="true">
                    <div className="wizard-template-title" />
                    {[0, 1, 2, 3].map((panelIndex) => (
                      <div className="wizard-template-panel" key={panelIndex}>
                        <span className="wizard-template-image" />
                        <span className="wizard-template-text" />
                      </div>
                    ))}
                  </div>
                  <div className="wizard-comic-type-copy">
                    <strong>{translations.classicFourPanelTemplate}</strong>
                    <span>{translations.onePage}</span>
                    <p>{translations.classicFourPanelTemplateDescription}</p>
                    <ul>
                      <li>{translations.templateTitlePlacement}</li>
                      <li>{translations.templateImagePlacement}</li>
                      <li>{translations.templateTextPlacement}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <p className="wizard-credit-notice">
              {translations.spendingCreditsDialog}
            </p>

            <div className="wizard-actions wizard-actions-end">
              <button
                type="button"
                className="wizard-primary-button"
                onClick={handleGenerateDialog}
                disabled={isGenerateDialogDisabled}
                title={
                  credits != null && credits < REQUIRED_CREDITS.GENERATE_DIALOG
                    ? translations.notEnoughCreditsDialog
                    : translations.spendingCreditsDialog
                }
              >
                {isDialogGenerationInProgress ? (
                  <>
                    <span className="spinner" />
                    {translations.generatingDialog}
                  </>
                ) : (
                  <>
                    {translations.createScript} <NextIcon />
                  </>
                )}
              </button>
            </div>
          </section>
        )}

        {currentStep === 2 && (
          <section className="wizard-panel wizard-script-panel">
            {inputType === INPUT_TYPES.upload && verificationDetailScores && (
              <div
                className={`wizard-verification-toolbar ${
                  overallVerificationScore === null
                    ? 'wizard-verification-toolbar--unknown'
                    : ''
                }`}
              >
                <button
                  type="button"
                  className="wizard-verification-toggle"
                  onClick={() =>
                    setIsVerificationDetailsOpen(
                      (currentValue) => !currentValue,
                    )
                  }
                  aria-expanded={isVerificationDetailsOpen}
                >
                  <span className="wizard-verification-icon">
                    {overallVerificationScore === null ? (
                      <WizardVerificationUnknownIcon />
                    ) : (
                      <WizardCheckIcon />
                    )}
                  </span>
                  <span className="wizard-verification-copy">
                    <span className="wizard-verification-label">
                      {overallVerificationScore === null
                        ? translations.verificationScoreUnavailableLabel
                        : translations.verificationScoreLabel.replace(
                            '__SCORE__',
                            String(overallVerificationScore),
                          )}
                    </span>
                    <span className="wizard-verification-track">
                      <span
                        className="wizard-verification-fill"
                        style={{
                          width: `${overallVerificationScore ?? 0}%`,
                        }}
                      />
                    </span>
                  </span>
                  <WizardVerificationChevronIcon className="wizard-verification-chevron" />
                </button>
              </div>
            )}

            {inputType === INPUT_TYPES.upload &&
              verificationDetailScores &&
              isVerificationDetailsOpen && (
                <div className="wizard-verification-card">
                  <div className="wizard-verification-card-header">
                    <strong>{translations.verificationDetailsTitle}</strong>
                    <p>{translations.verificationDetailsDescription}</p>
                  </div>
                  <ul className="wizard-verification-checklist">
                    {verificationChecklist.map((item) => (
                      <li
                        key={item.key}
                        tabIndex={0}
                        className={
                          item.score === null
                            ? 'wizard-verification-item--unknown'
                            : undefined
                        }
                      >
                        <span>{item.label}</span>
                        <span className="wizard-verification-item-score">
                          <span
                            className="wizard-verification-item-value"
                            aria-label={
                              item.score === null
                                ? translations.verificationDetailUnavailable
                                : undefined
                            }
                          >
                            {item.score === null ? '—' : `${item.score}/100`}
                          </span>
                          <span className="wizard-verification-track">
                            <span
                              className="wizard-verification-fill"
                              style={{ width: `${item.score ?? 0}%` }}
                            />
                          </span>
                          {item.score === null ? (
                            <WizardVerificationUnknownIcon className="wizard-verification-item-check" />
                          ) : (
                            <WizardCheckIcon className="wizard-verification-item-check" />
                          )}
                        </span>
                        <span className="wizard-verification-tooltip">
                          {item.score === null
                            ? translations.verificationDetailUnavailableTooltip
                            : item.tooltip}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            <div className="wizard-script-layout">
              <aside className="wizard-panel-role-list">
                {(messages[0] || []).map((message, panelIndex) => (
                  <div
                    className={`wizard-panel-role-item ${
                      inputErrors[0]?.[panelIndex] ? 'has-errors' : ''
                    }`}
                    key={storyboard.panels[panelIndex]?.role || panelIndex}
                  >
                    <span>{panelIndex + 1}</span>
                    <div>
                      <strong>
                        {
                          [
                            translations.panelIntroduction,
                            translations.panelKeyFact,
                            translations.panelExplanation,
                            translations.panelConclusion,
                          ][panelIndex]
                        }
                      </strong>
                      <p>{message || translations.emptyFrame}</p>
                    </div>
                  </div>
                ))}
              </aside>

              <div className="wizard-frame-editor">
                <div className="wizard-frame-editor-title">
                  <h2>{translations.fourPanelStory}</h2>
                </div>

                <label className="wizard-title-field">
                  <span>{translations.comicTitle}</span>
                  <input
                    type="text"
                    value={storyboard.title}
                    onChange={(event) => handleTitleChange(event.target.value)}
                    placeholder={translations.comicTitlePlaceholder}
                    maxLength={COMIC_TITLE_CHARACTER_LIMIT}
                  />
                  <span
                    className={`wizard-character-counter ${
                      isTitleLimitExceeded ? 'limit-exceeded' : ''
                    }`}
                  >
                    {titleLength}/{COMIC_TITLE_CHARACTER_LIMIT}
                  </span>
                </label>

                <div className="wizard-sentence-list">
                  {(messages[0] || []).map((message, messageIndex) => (
                    <div
                      className="wizard-sentence-row wizard-panel-sentence-row"
                      key={
                        storyboard.panels[messageIndex]?.role || messageIndex
                      }
                    >
                      <span>{messageIndex + 1}</span>
                      <div className="wizard-sentence-field">
                        <strong className="wizard-panel-role-label">
                          {
                            [
                              translations.panelIntroduction,
                              translations.panelKeyFact,
                              translations.panelExplanation,
                              translations.panelConclusion,
                            ][messageIndex]
                          }
                        </strong>
                        <textarea
                          value={message}
                          onChange={(event) =>
                            handleMessageChange(
                              0,
                              messageIndex,
                              event.target.value,
                            )
                          }
                          className={
                            inputErrors[0]?.[messageIndex] ? 'input-error' : ''
                          }
                          placeholder={translations.panelTextPlaceholder}
                        />
                        <span
                          className={`wizard-character-counter ${
                            inputErrors[0]?.[messageIndex]
                              ? 'limit-exceeded'
                              : ''
                          }`}
                        >
                          {message.length}/{bubbleMessageCharacterLimit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="wizard-rewrite-options">
                  <div className="wizard-preference-group">
                    <label>{translations.length}</label>
                    <div className="wizard-segmented-control">
                      {(['keep', 'shorter', 'longer'] as RewriteLength[]).map(
                        (option) => (
                          <button
                            key={option}
                            type="button"
                            className={rewriteLength === option ? 'active' : ''}
                            onClick={() => setRewriteLength(option)}
                          >
                            {translations[option]}
                          </button>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="wizard-preference-group">
                    <label>{translations.complexity}</label>
                    <div className="wizard-segmented-control">
                      {(
                        ['keep', 'simpler', 'scientific'] as RewriteComplexity[]
                      ).map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={
                            rewriteComplexity === option ? 'active' : ''
                          }
                          onClick={() => setRewriteComplexity(option)}
                        >
                          {translations[option]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="wizard-preference-group">
                    <label>{translations.tone}</label>
                    <div className="wizard-segmented-control">
                      {(
                        ['professional', 'friendly', 'funny'] as RewriteTone[]
                      ).map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={rewriteTone === option ? 'active' : ''}
                          onClick={() => setRewriteTone(option)}
                        >
                          {option === 'professional' &&
                            translations.toneProfessional}
                          {option === 'friendly' && translations.toneFriendly}
                          {option === 'funny' && translations.toneFunny}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {inputType === INPUT_TYPES.upload ? (
                  <div className="wizard-selected-source">
                    <span>{translations.selectedPdf}</span>
                    <div className="wizard-selected-source-card">
                      <UploadIcon />
                      <strong>
                        {file?.name || translations.noPdfSelected}
                      </strong>
                      <button
                        type="button"
                        className="wizard-outline-button"
                        onClick={() => setCurrentStep(1)}
                      >
                        {translations.changePdf}
                      </button>
                    </div>
                    <p>{translations.changePdfDescription}</p>
                  </div>
                ) : (
                  <label className="wizard-instruction-field">
                    <span>{translations.regenerateSourceDescription}</span>
                    <textarea
                      value={comicDescription}
                      onChange={(event) =>
                        setComicDescription(event.target.value)
                      }
                      placeholder={translations.comicDescriptionPlaceholder}
                      maxLength={MAX_COMIC_DESCRIPTION_CHARACTERS}
                    />
                    <span className="wizard-character-counter">
                      {comicDescription.length}/
                      {MAX_COMIC_DESCRIPTION_CHARACTERS}
                    </span>
                  </label>
                )}

                <label className="wizard-instruction-field">
                  <span>{translations.rewriteInstruction}</span>
                  <textarea
                    value={rewriteInstruction}
                    onChange={(event) =>
                      setRewriteInstruction(event.target.value)
                    }
                    placeholder={translations.rewriteInstructionPlaceholder}
                    maxLength={MAX_REGENERATION_INSTRUCTION_CHARACTERS}
                  />
                  <span className="wizard-character-counter">
                    {rewriteInstruction.length}/
                    {MAX_REGENERATION_INSTRUCTION_CHARACTERS}
                  </span>
                </label>

                <p className="wizard-credit-notice wizard-frame-credit-notice">
                  {translations.regenerateFrameCreditNotice.replace(
                    '__CREDITS__',
                    String(REQUIRED_CREDITS.GENERATE_DIALOG),
                  )}
                </p>

                <div className="wizard-frame-regenerate-row">
                  <button
                    type="button"
                    className="wizard-outline-button"
                    onClick={handleRegenerateFrame}
                    disabled={regeneratingFrameIndex !== null}
                  >
                    {regeneratingFrameIndex === selectedFrame ? (
                      <span className="spinner" />
                    ) : (
                      <RegenerateIcon />
                    )}
                    {translations.regenerateFrame}
                  </button>
                </div>
              </div>
            </div>

            <div className="wizard-actions">
              <button
                type="button"
                className="wizard-secondary-button"
                onClick={() => setCurrentStep(1)}
              >
                <PreviousIcon /> {translations.previousStep}
              </button>
              <button
                type="button"
                className="wizard-primary-button"
                onClick={() => setCurrentStep(3)}
                disabled={
                  messages.some((frame) =>
                    frame.some((message) => !message.trim()),
                  ) || inputErrors.some((errors) => errors.includes(true))
                }
              >
                {translations.continue} <NextIcon />
              </button>
            </div>
          </section>
        )}

        {currentStep === 3 && (
          <section className="wizard-panel wizard-images-panel">
            <div className="wizard-image-grid">
              {storyboard.panels.map((panel, panelIndex) => (
                <article className="wizard-image-card" key={panel.role}>
                  <div className="wizard-image-card-header">
                    <span>{panelIndex + 1}</span>
                    <strong>
                      {
                        [
                          translations.panelIntroduction,
                          translations.panelKeyFact,
                          translations.panelExplanation,
                          translations.panelConclusion,
                        ][panelIndex]
                      }
                    </strong>
                  </div>

                  <label className="wizard-image-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        handlePanelImageChange(
                          panelIndex,
                          event.target.files?.[0],
                        )
                      }
                    />
                    {panelImages[panelIndex] ? (
                      <img
                        src={panelImages[panelIndex] ?? ''}
                        alt={`${translations.panelImage} ${panelIndex + 1}`}
                      />
                    ) : (
                      <span>
                        <UploadIcon />
                        {translations.uploadPanelImage}
                      </span>
                    )}
                  </label>

                  {panelImageNames[panelIndex] && (
                    <div className="wizard-image-file-row">
                      <span>{panelImageNames[panelIndex]}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePanelImage(panelIndex)}
                      >
                        <RedeemCloseIcon />
                        {translations.removePanelImage}
                      </button>
                    </div>
                  )}

                  <label className="wizard-image-prompt-field">
                    <span>{translations.panelImagePrompt}</span>
                    <textarea
                      value={panelImagePrompts[panelIndex]}
                      onChange={(event) =>
                        handlePanelImagePromptChange(
                          panelIndex,
                          event.target.value,
                        )
                      }
                      placeholder={
                        panelImages[panelIndex]
                          ? translations.panelImagePromptDisabled
                          : translations.panelImagePromptPlaceholder
                      }
                      disabled={Boolean(panelImages[panelIndex])}
                    />
                  </label>
                </article>
              ))}
            </div>

            <p className="wizard-image-note">
              {translations.panelImageOptionalNote}
            </p>
            <p className="wizard-credit-notice">{imageStepCreditNotice}</p>

            <div className="wizard-actions">
              <button
                type="button"
                className="wizard-secondary-button"
                onClick={() => setCurrentStep(2)}
              >
                <PreviousIcon /> {translations.previousStep}
              </button>
              <button
                type="button"
                className="wizard-primary-button"
                onClick={() => setCurrentStep(4)}
              >
                {translations.continue} <NextIcon />
              </button>
            </div>
          </section>
        )}

        {currentStep === 4 && (
          <section className="wizard-panel wizard-design-panel">
            <div className="wizard-color-customizer">
              <div className="wizard-color-section">
                <div>
                  <h2>{translations.comicBackgroundColor}</h2>
                  <p>{translations.comicBackgroundColorDescription}</p>
                </div>
                <div className="wizard-color-preview-row">
                  <span
                    className="wizard-color-preview"
                    style={{ backgroundColor }}
                  />
                  <input
                    type="color"
                    value={getValidColorInputValue(
                      backgroundColor,
                      DEFAULT_BACKGROUND_COLOR,
                    )}
                    onChange={(event) => setBackgroundColor(event.target.value)}
                    aria-label={translations.comicBackgroundColor}
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(event) =>
                      setBackgroundColor(normalizeHexColor(event.target.value))
                    }
                    aria-label={translations.hexColor}
                  />
                </div>
                <div className="wizard-color-presets">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      type="button"
                      key={`background-${color}`}
                      className={
                        normalizeHexColor(backgroundColor) === color
                          ? 'active'
                          : ''
                      }
                      style={{ backgroundColor: color }}
                      onClick={() => setBackgroundColor(color)}
                      aria-label={`${translations.comicBackgroundColor} ${color}`}
                    />
                  ))}
                </div>
                <div className="wizard-rgb-controls">
                  {(['r', 'g', 'b'] as const).map((channel) => (
                    <label key={`background-${channel}`}>
                      <span>{channel.toUpperCase()}</span>
                      <input
                        type="number"
                        min="0"
                        max="255"
                        value={getRgbFromHex(backgroundColor)[channel]}
                        onChange={(event) =>
                          handleRgbColorChange(
                            'background',
                            channel,
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="wizard-color-section">
                <div>
                  <h2>{translations.comicTextColor}</h2>
                  <p>{translations.comicTextColorDescription}</p>
                </div>
                <button
                  type="button"
                  className="wizard-suggested-color"
                  onClick={() => setTextColor(suggestedTextColor)}
                >
                  <span
                    style={{
                      backgroundColor: suggestedTextColor,
                      borderColor:
                        suggestedTextColor === '#ffffff'
                          ? '#d6dce5'
                          : suggestedTextColor,
                    }}
                  />
                  {translations.useSuggestedTextColor} {suggestedTextColor}
                </button>
                <div className="wizard-color-preview-row">
                  <span
                    className="wizard-color-preview"
                    style={{ backgroundColor: textColor }}
                  />
                  <input
                    type="color"
                    value={getValidColorInputValue(
                      textColor,
                      suggestedTextColor,
                    )}
                    onChange={(event) => setTextColor(event.target.value)}
                    aria-label={translations.comicTextColor}
                  />
                  <input
                    type="text"
                    value={textColor}
                    onChange={(event) =>
                      setTextColor(normalizeHexColor(event.target.value))
                    }
                    aria-label={translations.hexColor}
                  />
                </div>
                <div className="wizard-color-presets">
                  {Array.from(
                    new Set([
                      suggestedTextColor,
                      '#111111',
                      '#ffffff',
                      '#1f4f7a',
                      '#8d252a',
                      '#2f6f4e',
                    ]),
                  ).map((color) => (
                    <button
                      type="button"
                      key={`text-${color}`}
                      className={
                        normalizeHexColor(textColor) === color ? 'active' : ''
                      }
                      style={{ backgroundColor: color }}
                      onClick={() => setTextColor(color)}
                      aria-label={`${translations.comicTextColor} ${color}`}
                    />
                  ))}
                </div>
                <div className="wizard-rgb-controls">
                  {(['r', 'g', 'b'] as const).map((channel) => (
                    <label key={`text-${channel}`}>
                      <span>{channel.toUpperCase()}</span>
                      <input
                        type="number"
                        min="0"
                        max="255"
                        value={getRgbFromHex(textColor)[channel]}
                        onChange={(event) =>
                          handleRgbColorChange(
                            'text',
                            channel,
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="wizard-color-section">
                <div>
                  <h2>{translations.comicFonts}</h2>
                  <p>{translations.comicFontsDescription}</p>
                </div>
                <div className="wizard-font-controls">
                  <label>
                    <span>{translations.comicTitleFont}</span>
                    <select
                      value={titleFont}
                      onChange={(event) => setTitleFont(event.target.value)}
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>{translations.comicBodyFont}</span>
                    <select
                      value={bodyFont}
                      onChange={(event) => setBodyFont(event.target.value)}
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div
                className="wizard-color-live-preview"
                style={{
                  backgroundColor,
                  color: textColor,
                }}
              >
                <strong style={{ fontFamily: titleFont }}>
                  {storyboard.title || translations.defaultComicTitle}
                </strong>
                <span style={{ fontFamily: bodyFont }}>
                  {messages[0]?.[0] || translations.panelTextPlaceholder}
                </span>
              </div>
            </div>

            <div className="wizard-actions">
              <button
                type="button"
                className="wizard-secondary-button"
                onClick={() => setCurrentStep(3)}
              >
                <PreviousIcon /> {translations.previousStep}
              </button>
              <button
                type="button"
                className="wizard-primary-button"
                onClick={handleSubmit}
                disabled={isGenerateDisabled}
                title={
                  credits != null && credits < comicGenerationCreditCost
                    ? getImageCreditText(
                        translations.notEnoughCreditsComic,
                        generatedPanelImageCount,
                        comicGenerationCreditCost,
                        translations.imageCountText,
                        translations.tokenCountText,
                      )
                    : undefined
                }
              >
                {isComicGenerationInProgress ? (
                  <>
                    <span className="spinner" />
                    {translations.generatingPDF}
                  </>
                ) : (
                  translations.generatePDF
                )}
              </button>
            </div>
          </section>
        )}
      </div>

      {pdfUrl && (
        <div className="pdf-preview">
          <h3 className="pdf-title">{translations.generatedPDF}</h3>
          <embed src={pdfUrl} type="application/pdf" className="pdf-embed" />
        </div>
      )}
    </div>
  );
};

const ComicForm: React.FC = () => {
  const { translations, language, changeLanguage } = useLanguage({
    en,
    sr,
    sl,
  });

  return (
    <AppShell
      page="comic"
      className="div-form-container"
      translations={translations}
      language={language}
      changeLanguage={changeLanguage}
    >
      <ComicFormContent translations={translations} />
    </AppShell>
  );
};

export default ComicForm;
